/**
 * Teachers Tab - Full CRUD for Studio Teachers
 *
 * Manages studio teachers for a schedule including create, read, update, delete
 * Supports linking to existing studio members with instructor role
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
  IconButton,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import LinkIcon from '@mui/icons-material/Link';
import PersonIcon from '@mui/icons-material/Person';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SchoolIcon from '@mui/icons-material/School';
import {
  useGetTeachersQuery,
  useGetStudioInstructorsQuery,
  useCreateTeacherMutation,
  useUpdateTeacherMutation,
  useDeleteTeacherMutation,
} from '../../../api/schedulingApi';
import type { Teacher, TeacherFormData } from '../../../types/scheduling';
import AvailabilityManager from '../components/AvailabilityManager';
import SpecializationsDialog from '../components/SpecializationsDialog';

interface TeachersTabProps {
  scheduleId: string;
  studioId: string;
}

interface TeacherFormErrors {
  teacher_name?: string;
  email?: string;
  phone?: string;
  user?: string;
}

const TeachersTab: React.FC<TeachersTabProps> = ({ scheduleId, studioId }) => {
  const { data: teachers, isLoading } = useGetTeachersQuery({
    scheduleId,
    studioId,
  });
  const { data: instructors, isLoading: isLoadingInstructors } =
    useGetStudioInstructorsQuery(studioId);
  const [createTeacher, { isLoading: isCreating }] = useCreateTeacherMutation();
  const [updateTeacher, { isLoading: isUpdating }] = useUpdateTeacherMutation();
  const [deleteTeacher, { isLoading: isDeleting }] = useDeleteTeacherMutation();

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState<Teacher | null>(null);
  const [deleteDialogTeacher, setDeleteDialogTeacher] =
    useState<Teacher | null>(null);
  const [availabilityTeacher, setAvailabilityTeacher] =
    useState<Teacher | null>(null);
  const [specializationsTeacher, setSpecializationsTeacher] =
    useState<Teacher | null>(null);

  // Form state
  const [formData, setFormData] = useState<TeacherFormData>({
    teacher_name: '',
    email: '',
    phone: '',
    notes: '',
    user: undefined,
  });

  const [formErrors, setFormErrors] = useState<TeacherFormErrors>({});

  const resetForm = () => {
    setFormData({
      teacher_name: '',
      email: '',
      phone: '',
      notes: '',
      user: undefined,
    });
    setFormErrors({});
  };

  const validateForm = (): boolean => {
    const errors: TeacherFormErrors = {};

    // If no user is linked, teacher name is required
    if (!formData.user && !formData.teacher_name.trim()) {
      errors.teacher_name = 'Teacher name is required for standalone teachers';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleOpenAddDialog = () => {
    resetForm();
    setIsAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setIsAddDialogOpen(false);
    resetForm();
  };

  const handleOpenEditDialog = (teacher: Teacher) => {
    setEditingTeacher(teacher);
    setFormData({
      teacher_name: teacher.teacher_name,
      email: teacher.email || '',
      phone: teacher.phone || '',
      notes: teacher.notes || '',
      user: teacher.user || undefined,
    });
    setFormErrors({});
  };

  const handleCloseEditDialog = () => {
    setEditingTeacher(null);
    resetForm();
  };

  const handleUserChange = (userId: string) => {
    if (!userId) {
      // User cleared the selection - reset to manual entry mode
      setFormData({
        ...formData,
        user: undefined,
        teacher_name: '',
        email: '',
        phone: '',
      });
      return;
    }

    // Find the selected instructor
    const selectedInstructor = instructors?.find(
      instructor => instructor.id === userId
    );

    if (selectedInstructor && selectedInstructor.user) {
      // Auto-fill fields from the selected instructor's user data
      // Use fallback to membership email if user.email is null
      setFormData({
        ...formData,
        user: selectedInstructor.user.id,
        teacher_name:
          `${selectedInstructor.user.first_name} ${selectedInstructor.user.last_name}`.trim(),
        email: selectedInstructor.user.email || selectedInstructor.email,
        phone: selectedInstructor.user.phone || '', // Use phone from user object if available
      });
    }
  };

  const handleCreate = async () => {
    if (!validateForm()) return;

    try {
      const dataToSend: TeacherFormData = {
        teacher_name: formData.teacher_name,
        email: formData.email || '',
        phone: formData.phone || '',
        notes: formData.notes || '',
      };

      // Only include user if it's set
      if (formData.user) {
        dataToSend.user = formData.user;
      }

      await createTeacher({
        scheduleId,
        studioId,
        data: dataToSend,
      }).unwrap();
      handleCloseAddDialog();
    } catch (error) {
      console.error('Failed to create teacher:', error);
    }
  };

  const handleUpdate = async () => {
    if (!editingTeacher || !validateForm()) return;

    try {
      const dataToSend: TeacherFormData = {
        teacher_name: formData.teacher_name,
        email: formData.email || '',
        phone: formData.phone || '',
        notes: formData.notes || '',
      };

      // Only include user if it's set
      if (formData.user) {
        dataToSend.user = formData.user;
      }

      await updateTeacher({
        id: editingTeacher.id,
        studioId,
        data: dataToSend,
      }).unwrap();
      handleCloseEditDialog();
    } catch (error) {
      console.error('Failed to update teacher:', error);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialogTeacher) return;

    try {
      await deleteTeacher({
        id: deleteDialogTeacher.id,
        studioId,
      }).unwrap();
      setDeleteDialogTeacher(null);
    } catch (error) {
      console.error('Failed to delete teacher:', error);
    }
  };

  if (isLoading || isLoadingInstructors) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  const isLinkedTeacher = !!formData.user;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Teachers</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAddDialog}
        >
          Add Teacher
        </Button>
      </Stack>

      {!teachers || teachers.length === 0 ? (
        <Alert severity="info">
          No teachers added yet. Click &quot;Add Teacher&quot; to create your
          first teacher entry.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Teacher Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Link Status</TableCell>
                <TableCell align="right">Management</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {teachers.map(teacher => (
                <TableRow key={teacher.id}>
                  <TableCell>{teacher.teacher_name}</TableCell>
                  <TableCell>{teacher.email || '—'}</TableCell>
                  <TableCell>{teacher.phone || '—'}</TableCell>
                  <TableCell>
                    {teacher.is_linked ? (
                      <Chip
                        icon={<LinkIcon />}
                        label={`Linked to ${teacher.user_full_name || 'User'}`}
                        color="primary"
                        size="small"
                      />
                    ) : (
                      <Chip
                        icon={<PersonIcon />}
                        label="Standalone"
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => setAvailabilityTeacher(teacher)}
                      title="Manage availability"
                      color="primary"
                    >
                      <ScheduleIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setSpecializationsTeacher(teacher)}
                      title="Manage specializations"
                      color="secondary"
                    >
                      <SchoolIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEditDialog(teacher)}
                      title="Edit teacher"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteDialogTeacher(teacher)}
                      title="Delete teacher"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Teacher Dialog */}
      <Dialog
        open={isAddDialogOpen}
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Teacher</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info" icon={<LinkIcon />}>
              <Typography variant="body2">
                Linking to a studio member allows automatic syncing of teacher
                info. Leave blank to create a standalone teacher entry.
              </Typography>
            </Alert>

            <FormControl fullWidth>
              <InputLabel>Link to Studio Member (Optional)</InputLabel>
              <Select
                value={formData.user || ''}
                onChange={e => handleUserChange(e.target.value)}
                label="Link to Studio Member (Optional)"
              >
                <MenuItem value="">
                  <em>None - Create Standalone Teacher</em>
                </MenuItem>
                {instructors?.map(instructor => (
                  <MenuItem key={instructor.id} value={instructor.id}>
                    {instructor.user
                      ? `${instructor.user.first_name} ${instructor.user.last_name} (${instructor.user.email || instructor.email})`
                      : instructor.email}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Select an instructor from your studio members
              </FormHelperText>
            </FormControl>

            <TextField
              fullWidth
              label="Teacher Name"
              value={formData.teacher_name}
              onChange={e =>
                setFormData({ ...formData, teacher_name: e.target.value })
              }
              error={!!formErrors.teacher_name}
              helperText={formErrors.teacher_name}
              required={!isLinkedTeacher}
              disabled={isLinkedTeacher}
              InputProps={{
                readOnly: isLinkedTeacher,
              }}
            />

            <TextField
              fullWidth
              type="email"
              label="Email"
              value={formData.email}
              onChange={e =>
                setFormData({ ...formData, email: e.target.value })
              }
              error={!!formErrors.email}
              helperText={
                formErrors.email ||
                (isLinkedTeacher ? 'Auto-filled from linked user' : 'Optional')
              }
              disabled={isLinkedTeacher}
              InputProps={{
                readOnly: isLinkedTeacher,
              }}
            />

            <TextField
              fullWidth
              type="tel"
              label="Phone"
              value={formData.phone}
              onChange={e =>
                setFormData({ ...formData, phone: e.target.value })
              }
              error={!!formErrors.phone}
              helperText={formErrors.phone || 'Optional'}
              disabled={isLinkedTeacher}
              InputProps={{
                readOnly: isLinkedTeacher,
              }}
            />

            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={e =>
                setFormData({ ...formData, notes: e.target.value })
              }
              multiline
              rows={3}
              helperText="Optional - Add any additional information"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog} disabled={isCreating}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={isCreating}
          >
            Add Teacher
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Teacher Dialog */}
      <Dialog
        open={!!editingTeacher}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Teacher</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info" icon={<LinkIcon />}>
              <Typography variant="body2">
                Linking to a studio member allows automatic syncing of teacher
                info. Leave blank to create a standalone teacher entry.
              </Typography>
            </Alert>

            <FormControl fullWidth>
              <InputLabel>Link to Studio Member (Optional)</InputLabel>
              <Select
                value={formData.user || ''}
                onChange={e => handleUserChange(e.target.value)}
                label="Link to Studio Member (Optional)"
              >
                <MenuItem value="">
                  <em>None - Create Standalone Teacher</em>
                </MenuItem>
                {instructors?.map(instructor => (
                  <MenuItem key={instructor.id} value={instructor.id}>
                    {instructor.user
                      ? `${instructor.user.first_name} ${instructor.user.last_name} (${instructor.user.email || instructor.email})`
                      : instructor.email}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Select an instructor from your studio members
              </FormHelperText>
            </FormControl>

            <TextField
              fullWidth
              label="Teacher Name"
              value={formData.teacher_name}
              onChange={e =>
                setFormData({ ...formData, teacher_name: e.target.value })
              }
              error={!!formErrors.teacher_name}
              helperText={formErrors.teacher_name}
              required={!isLinkedTeacher}
              disabled={isLinkedTeacher}
              InputProps={{
                readOnly: isLinkedTeacher,
              }}
            />

            <TextField
              fullWidth
              type="email"
              label="Email"
              value={formData.email}
              onChange={e =>
                setFormData({ ...formData, email: e.target.value })
              }
              error={!!formErrors.email}
              helperText={
                formErrors.email ||
                (isLinkedTeacher ? 'Auto-filled from linked user' : 'Optional')
              }
              disabled={isLinkedTeacher}
              InputProps={{
                readOnly: isLinkedTeacher,
              }}
            />

            <TextField
              fullWidth
              type="tel"
              label="Phone"
              value={formData.phone}
              onChange={e =>
                setFormData({ ...formData, phone: e.target.value })
              }
              error={!!formErrors.phone}
              helperText={formErrors.phone || 'Optional'}
              disabled={isLinkedTeacher}
              InputProps={{
                readOnly: isLinkedTeacher,
              }}
            />

            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={e =>
                setFormData({ ...formData, notes: e.target.value })
              }
              multiline
              rows={3}
              helperText="Optional - Add any additional information"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} disabled={isUpdating}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={isUpdating}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Availability Manager */}
      <AvailabilityManager
        open={!!availabilityTeacher}
        onClose={() => setAvailabilityTeacher(null)}
        entityType="teacher"
        entityId={availabilityTeacher?.id || ''}
        entityName={availabilityTeacher?.teacher_name || ''}
        studioId={studioId}
      />

      {/* Specializations Dialog */}
      <SpecializationsDialog
        open={!!specializationsTeacher}
        onClose={() => setSpecializationsTeacher(null)}
        teacherId={specializationsTeacher?.id || ''}
        teacherName={specializationsTeacher?.teacher_name || ''}
        studioId={studioId}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteDialogTeacher}
        onClose={() => setDeleteDialogTeacher(null)}
      >
        <DialogTitle>Delete Teacher?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete &quot;
            {deleteDialogTeacher?.teacher_name}&quot;? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogTeacher(null)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeachersTab;
